import React, { useCallback } from "react";
import { Button } from "@teals/ui-web";
import {
  StepContent,
  SuccessBanner,
  DetailSection,
  DetailSectionTitle,
  KeyDisplay,
  CopyButton,
  CodeBlock,
  WizardFooter,
  FooterActions,
} from "../styles";
import type { DataSource } from "../../../store/api/dataSourcesApi";

interface StepConnectionDetailsProps {
  createdDs: DataSource | null;
  apiBaseUrl: string;
  returnToAgent?: boolean;
  onUploadData: () => void;
  onViewAll: () => void;
}

const StepConnectionDetails: React.FC<StepConnectionDetailsProps> = ({
  createdDs,
  apiBaseUrl: propBaseUrl,
  returnToAgent = false,
  onUploadData,
  onViewAll,
}) => {
  const apiBaseUrl = propBaseUrl || window.location.origin;
  const endpoint = createdDs
    ? `${apiBaseUrl}/v1/data-sources/${createdDs.dataSourceId}/records`
    : "";

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  if (!createdDs) return null;

  const curlSnippet = `curl -X POST \\
  ${endpoint} \\
  -H "x-api-key: your-api-key" \\
  -H "x-secret-key: your-secret-key" \\
  -H "Content-Type: application/json" \\
  -d '{"title": "Example", "category": "demo"}'`;

  return (
    <StepContent>
      <SuccessBanner>{"\u2713"} Data source created successfully</SuccessBanner>
      <DetailSection>
        <DetailSectionTitle>Connection Details</DetailSectionTitle>
        <div>
          <label>Data Source ID</label>
          <KeyDisplay>
            {createdDs.dataSourceId}
            <CopyButton onClick={() => copyToClipboard(createdDs.dataSourceId)}>
              Copy
            </CopyButton>
          </KeyDisplay>
        </div>
        <div>
          <label>API Endpoint</label>
          <KeyDisplay>
            {endpoint}
            <CopyButton onClick={() => copyToClipboard(endpoint)}>
              Copy
            </CopyButton>
          </KeyDisplay>
        </div>
        <div>
          <label>Authentication</label>
          <p>
            Use your API Key Profile credentials: <code>x-api-key</code> and{" "}
            <code>x-secret-key</code> headers.
          </p>
        </div>
      </DetailSection>
      <DetailSectionTitle>Sample cURL</DetailSectionTitle>
      <CodeBlock>{curlSnippet}</CodeBlock>
      <WizardFooter>
        <FooterActions>
          {!returnToAgent && (
            <Button variant="secondary" onClick={onViewAll}>
              View all integrations
            </Button>
          )}
          <Button
            variant="primary"
            onClick={returnToAgent ? onViewAll : onUploadData}
          >
            {returnToAgent ? "Back to agent" : "View data source"}
          </Button>
        </FooterActions>
      </WizardFooter>
    </StepContent>
  );
};

export default StepConnectionDetails;
